import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

// Whop statuses that mean "a charge failed and Whop is retrying the card".
// During this window the user KEEPS access (see the reconcile grace policy in
// routes/payments.js) — we don't cut them off while Whop is still trying. This
// banner just nudges them to fix payment so they don't eventually get
// downgraded if Whop gives up. Card/payment is fixed in Whop's hosted billing
// portal (manageUrl) — we can't take card details ourselves (Whop owns PCI).
const PAYMENT_FAILED = new Set(['past_due', 'unpaid', 'incomplete']);
const WHOP_ORDERS_FALLBACK = 'https://whop.com/@me/settings/orders/';

export function PaymentBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState<string | null>(null);
  const [manageUrl, setManageUrl] = useState<string>(WHOP_ORDERS_FALLBACK);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .get<{ status?: string | null; manageUrl?: string }>('/api/payments/status')
      .then((res) => {
        if (cancelled) return;
        setStatus(res?.status ?? null);
        if (res?.manageUrl) setManageUrl(res.manageUrl);
      })
      .catch(() => {
        /* not subscribed / offline — no banner */
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user || dismissed || !status || !PAYMENT_FAILED.has(status)) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[100] shadow-md"
      style={{ backgroundColor: '#f59e0b', color: '#111827' }}
      role="alert"
    >
      <div className="mx-auto max-w-5xl px-4 py-2.5 flex items-center gap-3 text-sm">
        <span className="font-semibold">⚠️ Your last payment didn’t go through.</span>
        <span className="hidden sm:inline">
          Update your payment method to keep your access — we’ll keep retrying in the meantime.
        </span>
        <a
          href={manageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto shrink-0 rounded-md px-3 py-1.5 font-semibold"
          style={{ backgroundColor: '#111827', color: '#ffffff' }}
        >
          Update payment →
        </a>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
          className="shrink-0 rounded px-1.5 font-bold"
          style={{ color: 'rgba(17,24,39,0.7)' }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
